import { MOCK_CONVERSATIONS, MOCK_MESSAGES } from '../mock-data/index.js';
import { delay } from '../utils/helpers.js';

export class LiveChatService {
	constructor(baseAPI) {
		this.api = baseAPI;
	}

	async getLiveChatSettings() {
		await this.api._ensureSession();

		if (this.api.mock) {
			return {
				status: true,
				data: {
					enabled: true,
					greeting_message: 'Hi there! How can we help?',
					team_name: 'Support Team',
					response_time: 'Usually replies within a few minutes',
				},
			};
		}

		return this.api._makeRequest('/widget/livechat/settings', {
			method: 'GET',
			headers: { Authorization: `Bearer ${this.api.sessionToken}` },
		});
	}

	async checkAgentsOnline() {
		await this.api._ensureSession();

		if (this.api.mock) {
			return {
				status: true,
				data: {
					agents_online: true,
					online_count: 2,
					response_time: 'Usually replies within a few minutes',
					available_agents: [
						{ full_name: 'Sarah', picture: '' },
						{ full_name: 'Tom', picture: '' },
					],
				},
			};
		}

		return this.api._makeRequest('/widget/livechat/agents/online', {
			method: 'GET',
			headers: { Authorization: `Bearer ${this.api.sessionToken}` },
		});
	}

	async getConversations(options = {}) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(300);
			return {
				status: true,
				data: MOCK_CONVERSATIONS,
				meta: { total: MOCK_CONVERSATIONS.length, page: 1, limit: 20 },
			};
		}

		const endpoint = this.api._getEndpointWithParams(
			'/widget/livechat/conversations',
			options
		);
		return this.api._makeRequest(endpoint, {
			method: 'GET',
			headers: { Authorization: `Bearer ${this.api.sessionToken}` },
		});
	}

	async getConversation(conversationId) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(200);
			const conv = MOCK_CONVERSATIONS.find((c) => c.id === conversationId);
			return {
				status: true,
				data: { ...conv, messages: MOCK_MESSAGES[conversationId] || [] },
			};
		}

		return this.api._makeRequest(
			`/widget/livechat/conversations/${conversationId}`,
			{
				method: 'GET',
				headers: { Authorization: `Bearer ${this.api.sessionToken}` },
			}
		);
	}

	async startConversation(data) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(300);
			const newConv = {
				id: 'conv_' + Date.now(),
				subject: data.subject || 'New conversation',
				status: 'open',
				last_message_at: new Date().toISOString(),
				created_at: new Date().toISOString(),
				messages: [
					{
						id: 'msg_' + Date.now(),
						content: data.message,
						sender_type: 'customer',
						created_at: new Date().toISOString(),
						attachments: data.attachments || [],
					},
				],
			};
			MOCK_CONVERSATIONS.unshift(newConv);
			MOCK_MESSAGES[newConv.id] = newConv.messages;
			return { status: true, data: newConv };
		}

		return this.api._makeRequest('/widget/livechat/conversations', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.api.sessionToken}`,
			},
			body: JSON.stringify({
				message: data.message,
				subject: data.subject || '',
				attachments: data.attachments || [],
			}),
		});
	}

	async sendMessage(conversationId, data) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(200);
			const newMessage = {
				id: 'msg_' + Date.now(),
				content: data.content,
				sender_type: 'customer',
				created_at: new Date().toISOString(),
				attachments: data.attachments || [],
			};
			if (!MOCK_MESSAGES[conversationId]) {
				MOCK_MESSAGES[conversationId] = [];
			}
			MOCK_MESSAGES[conversationId].push(newMessage);
			return { status: true, data: newMessage };
		}

		return this.api._makeRequest(
			`/widget/livechat/conversations/${conversationId}/messages`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${this.api.sessionToken}`,
				},
				body: JSON.stringify({
					content: data.content,
					attachments: data.attachments || [],
				}),
			}
		);
	}

	async getUnreadCount() {
		await this.api._ensureSession();

		if (this.api.mock) {
			const count = MOCK_CONVERSATIONS.reduce(
				(sum, c) => sum + (c.unread || 0),
				0
			);
			return {
				status: true,
				data: { unread_count: count, unread_conversations: count > 0 ? 1 : 0 },
			};
		}

		return this.api._makeRequest('/widget/livechat/unread', {
			method: 'GET',
			headers: { Authorization: `Bearer ${this.api.sessionToken}` },
		});
	}

	async markConversationAsRead(conversationId) {
		await this.api._ensureSession();

		if (this.api.mock) {
			return { status: true };
		}

		return this.api._makeRequest(
			`/widget/livechat/conversations/${conversationId}/read`,
			{
				method: 'POST',
				headers: { Authorization: `Bearer ${this.api.sessionToken}` },
			}
		);
	}

	async getMessages(conversationId, options = {}) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(200);
			return {
				status: true,
				data: MOCK_MESSAGES[conversationId] || [],
				meta: { total: 0, page: 1, limit: 50 },
			};
		}

		const params = { ...options };
		const endpoint = this.api._getEndpointWithParams(
			`/widget/livechat/conversations/${conversationId}/messages`,
			params
		);
		return this.api._makeRequest(endpoint, {
			method: 'GET',
			headers: { Authorization: `Bearer ${this.api.sessionToken}` },
		});
	}

	async uploadFile(base64Data, filename) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(300);
			return { status: true, url: `https://mock-cdn.example.com/${filename}` };
		}

		return this.api._makeRequest('/widget/livechat/upload', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.api.sessionToken}`,
			},
			body: JSON.stringify({ file: base64Data, filename }),
		});
	}

	async identifyContact(data) {
		return this.api.identify(data);
	}

	async submitRating(conversationId, data) {
		await this.api._ensureSession();

		if (this.api.mock) {
			return { status: true, data: { rated: true } };
		}

		return this.api._makeRequest(
			`/widget/livechat/conversations/${conversationId}/rating`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${this.api.sessionToken}`,
				},
				body: JSON.stringify(data),
			}
		);
	}

	async sendTypingIndicator(conversationId, isTyping) {
		await this.api._ensureSession();

		if (this.api.mock) {
			return { status: true };
		}

		return this.api._makeRequest(
			`/widget/livechat/conversations/${conversationId}/typing`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${this.api.sessionToken}`,
				},
				body: JSON.stringify({ is_typing: isTyping }),
			}
		);
	}
}
